import { useEffect, useRef } from "react";
import {
  onSaveOrderData,
  onSavePoints,
} from "../../../Store/Slices/cardDataSlice";
import {
  onPendingOrderReset,
  onPostOrder,
  onPostOrderReset,
  onUpdateOrder,
  onUpdateOrderReset,
} from "../../../Store/Slices/orderSlice";
import {
  ApiRespCode,
  handleError,
} from "../../Utility/Utility";
import { callSwitchAccountApi } from "../../../Context/switchAccountApi";
import { buildUpdateOrderPayload } from "../utils/buildUpdateOrderPayload";
import { usePaymentGateway } from "./usePaymentGateway";
import { calculateGrossPayableAmount } from "./useCartPricing";

export function useOrderFlow({
  dispatch,
  getOrderPayload,
  orderData,
  flow2,
  totalPayableAmount,
  cash,
  convenienceFee,
  gst,
  setPoints,
  setBurnPoints,
  setCash,
  setTotalPayableAmount,
  setLoading,
  setCardBalances,
  journeyType,
  loginV3Data,
  validateLoginTokenData,
  selectedCardBasedRedemtion,
  razorPayOfferCode,
  payuOfferCode,
  MatchedCardMasterData,
  paymentConfig,
  productReducer,
  updatedData,
  burnPoints,
  earnPoints,
  translation,
  isHtmlHostedPayu,
  surlLocation,
}) {
  // reset points slider and refresh card balance after payment dismissal
  const handleSet = () => {
    const fullPayableAmount = calculateGrossPayableAmount(
      updatedData,
      convenienceFee,
      gst,
    );

    setPoints(0);
    setBurnPoints(0);
    if (typeof setCash === "function") {
      setCash(fullPayableAmount);
    }
    setTotalPayableAmount(fullPayableAmount);
    if (journeyType === false) {
      (async () => {
        try {
          const data = await callSwitchAccountApi({
            identifier: loginV3Data?.memberIdentifier,
            subIdentifier:
              selectedCardBasedRedemtion?.identifier ||
              validateLoginTokenData?.subIdentifier,
            identifierType:
              JSON.parse(selectedCardBasedRedemtion?.identifiertype) ?? 1002,
            isAccountSwitch: true,
            userSessionToken: sessionStorage.getItem("loginSessionToken"),
          });
          const cardIdentifier =
            selectedCardBasedRedemtion?.identifier ||
            validateLoginTokenData?.subIdentifier;
          if (data?.errorCode === "200") {
            const pointBalance = Math.round(
              data?.response?.response?.memberBalance?.pointBalance ?? 0,
            );
            dispatch(
              onSavePoints({
                pointBalance: parseInt(pointBalance),
                identifierType:
                  JSON.parse(selectedCardBasedRedemtion?.identifiertype) ??
                  1002,
                subIdentifier: cardIdentifier,
              }),
            );
            setCardBalances((prev) => ({
              ...prev,
              [cardIdentifier]: {
                loading: false,
                failed: false,
                points: pointBalance,
              },
            }));
          } else {
            setCardBalances((prev) => ({
              ...prev,
              [cardIdentifier]: {
                loading: false,
                failed: true,
                points: null,
              },
            }));
          }
        } catch {
          setCardBalances((prev) => ({
            ...prev,
            [selectedCardBasedRedemtion?.identifier ||
              validateLoginTokenData?.subIdentifier]: {
              loading: false,
              failed: true,
              points: null,
            },
          }));
        }
      })();
    }
  };

  // update order after payment result
  const handleUpdateOrder = async (
    transaction,
    paymentVerify = false,
    pointEarn = false,
    orderCancelled = false,
    orderFailed = false,
    paymentFailedStatus,
    hosted = false,
  ) => {
    const payuHostedIntegration = hosted;
    const existingPayload = sessionStorage.getItem("OrderPayloadSession");
    let savedOrderResponse = null;
    try {
      savedOrderResponse = JSON.parse(
        sessionStorage.getItem("OrderPayloadSession_post") || "null",
      );
    } catch {
      savedOrderResponse = null;
    }
    let payload;
    if (payuHostedIntegration && paymentVerify === true) {
      const existingTransactionStrHook = sessionStorage.getItem('OrderPayloadSession_post');
      const existingTransactionDataHook = existingTransactionStrHook ? JSON.parse(existingTransactionStrHook) : null;
      const postorderData = existingTransactionDataHook;
      const existingPayloadParsed = existingPayload ? JSON.parse(existingPayload) : null;
      payload = buildUpdateOrderPayload({
        existingPayload: { ...existingPayloadParsed, orderStatus: postorderData?.orderStatus, paymentStatus: postorderData?.paymentStatus },
        basePayload: getOrderPayload(),
        transaction,
        paymentVerify,
        orderCancelled,
        orderFailed,
        paymentFailedStatus,
        flow2,
        totalPayableAmount,
        orderId: orderData?.postOrderData?.[0]?.id ?? savedOrderResponse?.id,
        correlationId:
          orderData?.postOrderData?.[0]?.correlationId ??
          savedOrderResponse?.correlationId,
      });
    } else {
      payload = buildUpdateOrderPayload({
        existingPayload: existingPayload ? JSON.parse(existingPayload) : null,
        basePayload: getOrderPayload(),
        transaction,
        paymentVerify,
        orderCancelled,
        orderFailed,
        paymentFailedStatus,
        flow2,
        totalPayableAmount,
        orderId: orderData?.postOrderData?.[0]?.id ?? savedOrderResponse?.id,
        correlationId:
          orderData?.postOrderData?.[0]?.correlationId ??
          savedOrderResponse?.correlationId,
      });
    }
    sessionStorage.setItem("OrderPayloadSession", JSON.stringify(payload));

    await dispatch(onUpdateOrder(payload));
  };

  // create a new order
  const handleOrder = () => {
    const orderPayload = getOrderPayload();
    sessionStorage.setItem("OrderPayloadSession", JSON.stringify(orderPayload));
    dispatch(onPendingOrderReset());
    dispatch(onPostOrderReset());
    dispatch(onUpdateOrderReset());
    dispatch(onPostOrder(orderPayload));
  };

  // payment gateway hook
  const { initiatePayment } = usePaymentGateway({
    orderData,
    totalPayableAmount,
    burnPoints,
    loginV3Data,
    productReducer,
    razorPayOfferCode,
    payuOfferCode,
    MatchedCardMasterData,
    paymentConfig,
    setLoading,
    onUpdateOrder: handleUpdateOrder,
    onReset: handleSet,
    isHtmlHostedPayu,
    updatedData,
    surlLocation,
    getOrderPayload,
    flow2
  });

  const handlePayment = () => {
    initiatePayment();
  };
  // watch order creation and update responses
  useEffect(() => {
    const orderCreated = orderData?.post_status_code === ApiRespCode.create;
    const orderCreateResp = orderData?.post_status_code;

    if (orderCreated) {
      if (totalPayableAmount === 0) {
        setLoading(true);
        handleUpdateOrder(
          undefined,
          false,
          false,
          false,
          false,
          orderData?.updatedOrderData?.paymentStatus,
        );
      } else {
        // Persist order payload so we can recover/verify payment after a hosted gateway redirect
        try {
          const basePayload = getOrderPayload && typeof getOrderPayload === 'function' ? getOrderPayload() : null;
          if (basePayload) {
            sessionStorage.setItem("OrderPayloadSession", JSON.stringify(basePayload));
          }
        } catch (e) {
          // ignore storage errors
          // eslint-disable-next-line no-console
          console.warn('Could not persist OrderPayloadSession before redirect', e);
        }
        handlePayment();
      }
      const orderDetails = {
        tncContent: productReducer?.productReducerDisocount?.tncContent,
        totalPayableAmount,
        burnPoints,
        orderId: orderData?.postOrderData?.[0]?.orderId,
        name: productReducer?.productReducerDisocount?.name,
        id: productReducer?.productReducerDisocount?.id,
        quantity: productReducer?.arrayData?.arrayData?.[0]?.quantity,
        orderItems: updatedData,
        earnPoints,
        cardName: productReducer?.productReducerDisocount?.name,
        cardImage: productReducer?.productReducerDisocount?.small,
      };
      dispatch(onSaveOrderData(orderDetails));
      dispatch(onPostOrderReset());
      return;
    } else if (orderCreateResp) {
      if (
        orderData?.errorCode === "1456" ||
        orderData?.errorCode === "1443"
      ) {
        handleError(
          translation.Selected_denomination_currently_out_stock ||
          "Selected denomination is currently out of stock",
        );
      } else {
        handleError(
          "Oops! We couldn't place your order right now. Please try again in a few moments.",
        );
      }
      dispatch(onPostOrderReset());
    }
    if (orderData?.errorCode === "1468") {
      handleError(translation.order_cancelled_by_customer_msg, 10000);
      dispatch(onUpdateOrderReset());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderData]);

  return { handleOrder, handleUpdateOrder, handleSet, handlePayment };
}
